import { useCallback, useEffect, useState } from "react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { ForbiddenPanel } from "../../components/admin/ForbiddenPanel";
import { authedFetch } from "../../lib/api-client";
import { useT } from "../../lib/use-t";
import type {
  ActiveIssue,
  CostsSnapshot,
  ElevenLabsUsage,
  HealthSnapshot,
  ProviderIndicator,
  ProviderStatus,
} from "../../lib/admin/types";

function relativeFromNow(ts: number): string {
  const diffMs = Date.now() - ts;
  if (diffMs < 60_000) {
    const s = Math.max(1, Math.floor(diffMs / 1000));
    return `${s}s ago`;
  }
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)} min ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)} h ago`;
  return `${Math.floor(diffMs / 86_400_000)} d ago`;
}

function relativeUntil(ts: number): string {
  const diffMs = ts * 1000 - Date.now();
  if (diffMs < 0) return "expired";
  if (diffMs < 86_400_000) return `in ${Math.max(1, Math.floor(diffMs / 3_600_000))} h`;
  return `in ${Math.floor(diffMs / 86_400_000)} d`;
}

function indicatorColor(i: ProviderIndicator): string {
  switch (i) {
    case "ok":
      return "var(--pass)";
    case "minor":
      return "var(--warn)";
    case "major":
    case "critical":
      return "var(--fail)";
    case "unknown":
      return "var(--ink-faint)";
  }
}

function formatUsd(n: number): string {
  return n.toLocaleString("nl-NL", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

function CostCard({
  label,
  spent,
  callCount,
  tokens,
  budget,
  customBar,
}: {
  label: string;
  spent: number | null;
  callCount: number | null;
  tokens: string;
  budget: number | null;
  customBar?: { pct: number } | null;
}) {
  const t = useT();
  const pct = customBar
    ? customBar.pct
    : spent !== null && budget !== null && budget > 0
      ? Math.min(100, Math.round((spent / budget) * 100))
      : null;
  const band: ProviderIndicator =
    pct === null ? "unknown" : pct >= 95 ? "critical" : pct >= 80 ? "minor" : "ok";
  const bandColor =
    band === "critical"
      ? "var(--fail)"
      : band === "minor"
        ? "var(--warn)"
        : band === "ok"
          ? "var(--pass)"
          : "var(--ink-faint)";
  return (
    <article className="rounded-lg border border-[var(--border)] bg-[var(--panel)] px-4 py-3 sm:px-5 sm:py-4 space-y-2">
      <header className="flex items-baseline justify-between gap-2">
        <span className="font-mono-badge text-xs text-[var(--ink-muted)]">{label}</span>
        {callCount !== null ? (
          <span className="text-[10px] text-[var(--ink-faint)] font-mono-badge">
            {callCount} call{callCount === 1 ? "" : "s"}
          </span>
        ) : null}
      </header>
      <div className="text-2xl font-serif-italic text-[var(--ink)] tabular-nums">
        {spent !== null ? formatUsd(spent) : <span className="text-[var(--ink-muted)]">—</span>}
      </div>
      <p className="text-xs text-[var(--ink-faint)] truncate">{tokens}</p>
      {pct !== null ? (
        <>
          <div
            aria-label={`${label} ${pct}%`}
            className="h-1.5 rounded-full bg-[var(--panel-soft)] overflow-hidden"
          >
            <div style={{ width: `${pct}%`, background: bandColor }} className="h-full" />
          </div>
          {budget !== null ? (
            <p className="text-[10px] text-[var(--ink-faint)] font-mono-badge">
              {formatUsd(spent ?? 0)} / {formatUsd(budget)} ({pct}%)
            </p>
          ) : (
            <p className="text-[10px] text-[var(--ink-faint)] font-mono-badge">{pct}%</p>
          )}
        </>
      ) : (
        <p className="text-[10px] text-[var(--ink-faint)] italic">
          {label === "Anthropic" || label === "Google"
            ? t("admin.health.budgetOptionalNote")
            : "—"}
        </p>
      )}
    </article>
  );
}

function CostsSection({
  costs,
  elevenlabs,
}: {
  costs: CostsSnapshot;
  elevenlabs: ElevenLabsUsage | null;
}) {
  const t = useT();
  const anthropic = costs.monthlyByProvider.find((p) => p.provider === "anthropic");
  const google = costs.monthlyByProvider.find((p) => p.provider === "google");
  return (
    <section aria-labelledby="costs" className="space-y-3">
      <div className="flex items-baseline justify-between gap-4">
        <h2
          id="costs"
          className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
        >
          {t("admin.health.costsHeading")}
        </h2>
        <span className="text-[10px] text-[var(--ink-faint)]">
          {t("admin.health.costsDisclaimer")}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <CostCard
          label="Anthropic"
          spent={anthropic?.estimatedCostUsd ?? 0}
          callCount={anthropic?.callCount ?? 0}
          tokens={
            anthropic
              ? `${(anthropic.inputTokens + anthropic.outputTokens).toLocaleString("nl-NL")} tokens`
              : t("admin.health.noCallsThisMonth")
          }
          budget={costs.budgets.anthropic}
        />
        <CostCard
          label="Google"
          spent={google?.estimatedCostUsd ?? 0}
          callCount={google?.callCount ?? 0}
          tokens={
            google
              ? `${(google.inputTokens + google.outputTokens).toLocaleString("nl-NL")} tokens`
              : t("admin.health.noCallsThisMonth")
          }
          budget={costs.budgets.google}
        />
        <CostCard
          label="ElevenLabs"
          spent={null}
          callCount={null}
          tokens={
            elevenlabs
              ? `${elevenlabs.characterCount.toLocaleString("nl-NL")} / ${elevenlabs.characterLimit.toLocaleString("nl-NL")} chars`
              : t("admin.health.noCallsThisMonth")
          }
          budget={null}
          customBar={
            elevenlabs
              ? {
                  pct: Math.min(
                    100,
                    Math.round((elevenlabs.characterCount / elevenlabs.characterLimit) * 100),
                  ),
                }
              : null
          }
        />
      </div>
    </section>
  );
}

function ActiveIssueBanner({ issue }: { issue: ActiveIssue }) {
  const t = useT();
  const tone =
    issue.severity === "critical"
      ? {
          border: "border-[var(--fail)]/40",
          chip: "text-[var(--fail)]",
          dot: "var(--fail)",
        }
      : {
          border: "border-[var(--warn)]/40",
          chip: "text-[var(--warn)]",
          dot: "var(--warn)",
        };
  return (
    <article
      role="alert"
      className={`rounded-lg border ${tone.border} bg-[var(--panel)] px-4 py-3 sm:px-5 sm:py-4 space-y-2`}
    >
      <header className="flex items-center gap-2">
        <span
          aria-hidden
          style={{ background: tone.dot }}
          className="inline-block w-2 h-2 rounded-full"
        />
        <span className={`font-mono-badge text-[10px] uppercase tracking-wider ${tone.chip}`}>
          {issue.severity === "critical" ? t("admin.health.issueSeverityCritical") : t("admin.health.issueSeverityWarning")}
        </span>
        <h3 className="text-sm font-semibold text-[var(--ink)]">{issue.title}</h3>
        <span className="ml-auto text-[10px] text-[var(--ink-faint)] font-mono-badge">
          {issue.count}× · {relativeFromNow(issue.lastSeenAt)}
        </span>
      </header>
      <p className="text-xs text-[var(--ink-muted)] leading-relaxed">{issue.description}</p>
    </article>
  );
}

function ProviderRow({ p }: { p: ProviderStatus }) {
  return (
    <div className="grid grid-cols-[7rem_auto_1fr] gap-x-4 gap-y-1 items-baseline">
      <span className="font-mono-badge text-xs text-[var(--ink-muted)]">{p.name}</span>
      <span className="inline-flex items-center gap-2 text-xs font-mono-badge">
        <span
          aria-hidden
          style={{ background: indicatorColor(p.indicator) }}
          className="inline-block w-2 h-2 rounded-full"
        />
        <span className="text-[var(--ink-muted)]">{p.indicator}</span>
      </span>
      <div className="min-w-0">
        <p className="text-sm text-[var(--ink)] truncate">{p.description}</p>
        {p.detail ? (
          <p className="text-xs text-[var(--ink-faint)] truncate mt-0.5">{p.detail}</p>
        ) : null}
      </div>
    </div>
  );
}

function HealthRefresh({ onRefresh, pending }: { onRefresh: () => void; pending: boolean }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onRefresh}
      disabled={pending}
      className="text-xs font-mono-badge text-[var(--ink-muted)] border border-[var(--border)] rounded-md px-2.5 py-1.5 hover:border-[var(--accent)] hover:text-[var(--ink)] disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {pending ? t("admin.health.refreshBusy") : `↻ ${t("admin.health.refreshButton")}`}
    </button>
  );
}

type LoadState =
  | { status: "loading" }
  | { status: "ok"; snap: HealthSnapshot }
  | { status: "forbidden" }
  | { status: "error"; message: string };

export function AdminHealthPage() {
  const t = useT();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (force: boolean) => {
    if (force) setRefreshing(true);
    try {
      const r = await authedFetch(`/admin/health${force ? "?force=1" : ""}`, { cache: "no-store" });
      if (r.status === 403) {
        setState({ status: "forbidden" });
        return;
      }
      const body = await r.json();
      if (!body.ok) {
        setState({
          status: "error",
          message: body.error?.message ?? `Server returned ${r.status}.`,
        });
        return;
      }
      setState({ status: "ok", snap: body.data });
    } catch (err) {
      setState({
        status: "error",
        message: err instanceof Error ? err.message : t("chrome.common.unknownError"),
      });
    } finally {
      if (force) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load(false);
  }, [load]);

  return (
    <AdminLayout>
      {state.status === "loading" ? (
        <p className="text-sm text-[var(--ink-faint)]">{t("chrome.common.loadingEllipsis")}</p>
      ) : state.status === "forbidden" ? (
        <ForbiddenPanel />
      ) : state.status === "error" ? (
        <p
          role="alert"
          className="rounded-md border border-[var(--fail)] bg-[var(--fail)]/10 px-3 py-2 text-sm text-[var(--fail)]"
        >
          {state.message}
        </p>
      ) : (
        <HealthBody snap={state.snap} onRefresh={() => void load(true)} refreshing={refreshing} />
      )}
    </AdminLayout>
  );
}

function HealthBody({
  snap,
  onRefresh,
  refreshing,
}: {
  snap: HealthSnapshot;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  const t = useT();
  const eleven = snap.elevenlabs;
  const usagePct = eleven
    ? Math.min(100, Math.round((eleven.characterCount / eleven.characterLimit) * 100))
    : 0;
  const usageBand: ProviderIndicator = !eleven
    ? "unknown"
    : usagePct >= 95
      ? "critical"
      : usagePct >= 80
        ? "minor"
        : "ok";

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
            {t("admin.health.eyebrow")}
          </p>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("admin.health.title")}</h1>
          <p className="text-sm text-[var(--ink-muted)]">
            {t("admin.health.lastCheckFmt", { time: relativeFromNow(snap.fetchedAt) })}
          </p>
        </div>
        <HealthRefresh onRefresh={onRefresh} pending={refreshing} />
      </header>

      <CostsSection costs={snap.costs} elevenlabs={snap.elevenlabs} />

      {snap.activeIssues.length > 0 ? (
        <section aria-labelledby="issues" className="space-y-3">
          <h2
            id="issues"
            className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
          >
            {t("admin.health.issuesHeading")}
          </h2>
          <div className="space-y-3">
            {snap.activeIssues.map((issue) => (
              <ActiveIssueBanner key={issue.id} issue={issue} />
            ))}
          </div>
        </section>
      ) : null}

      <section aria-labelledby="providers" className="space-y-4">
        <h2
          id="providers"
          className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
        >
          {t("admin.health.providersHeading")}
        </h2>
        <div className="rounded-lg border border-[var(--border)] bg-[var(--panel)] divide-y divide-[var(--border)]">
          {snap.providers.map((p) => (
            <div key={p.name} className="px-4 py-3 sm:px-5 sm:py-4">
              <ProviderRow p={p} />
            </div>
          ))}
          {eleven ? (
            <div className="px-4 py-3 sm:px-5 sm:py-4 space-y-2">
              <div className="grid grid-cols-[7rem_auto_1fr] gap-x-4 gap-y-1 items-baseline">
                <span className="font-mono-badge text-xs text-[var(--ink-muted)]">
                  ElevenLabs
                </span>
                <span className="inline-flex items-center gap-2 text-xs font-mono-badge">
                  <span
                    aria-hidden
                    style={{ background: indicatorColor(usageBand) }}
                    className="inline-block w-2 h-2 rounded-full"
                  />
                  <span className="text-[var(--ink-muted)]">
                    {eleven.tier}
                    {eleven.status && eleven.status !== "active" ? ` · ${eleven.status}` : ""}
                  </span>
                </span>
                <div className="min-w-0">
                  <p className="text-sm text-[var(--ink)]">
                    {eleven.characterCount.toLocaleString("nl-NL")} /{" "}
                    {eleven.characterLimit.toLocaleString("nl-NL")} chars ({usagePct}%)
                  </p>
                  <p className="text-xs text-[var(--ink-faint)] mt-0.5">
                    {t("admin.health.resetFmt", { time: relativeUntil(eleven.resetAtUnix) })}
                  </p>
                </div>
              </div>
              <div
                aria-label={`ElevenLabs character usage ${usagePct}%`}
                className="h-1.5 rounded-full bg-[var(--panel-soft)] overflow-hidden"
              >
                <div
                  style={{ width: `${usagePct}%`, background: indicatorColor(usageBand) }}
                  className="h-full"
                />
              </div>
            </div>
          ) : (
            <div className="px-4 py-3 sm:px-5 sm:py-4 text-xs text-[var(--ink-faint)] font-mono-badge">
              {t("admin.health.elevenLabsUnavailable")}
            </div>
          )}
        </div>
      </section>

      <section aria-labelledby="events" className="space-y-3">
        <div className="flex items-baseline justify-between gap-4">
          <h2
            id="events"
            className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
          >
            {t("admin.health.recentEventsHeading")}
          </h2>
          <span className="text-[10px] text-[var(--ink-faint)]">
            {t("admin.health.recentEventsDisclaimer")}
          </span>
        </div>
        {snap.events.length === 0 ? (
          <p className="text-sm text-[var(--ink-muted)] italic">
            {t("admin.health.noRecentEvents")}
          </p>
        ) : (
          <ul className="rounded-lg border border-[var(--border)] bg-[var(--panel)] divide-y divide-[var(--border)]">
            {snap.events.map((ev, idx) => (
              <li
                key={`${ev.at}-${idx}`}
                className="px-4 py-3 sm:px-5 grid grid-cols-[6rem_8rem_1fr] gap-x-4 items-baseline text-xs"
              >
                <span className="text-[var(--ink-faint)] font-mono-badge">
                  {relativeFromNow(ev.at)}
                </span>
                <span className="font-mono-badge text-[var(--ink-muted)] truncate">
                  {ev.kind}
                  {ev.provider ? ` · ${ev.provider}` : ""}
                </span>
                <span className="text-[var(--ink)] truncate">{ev.message}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
